import React, { useState, useEffect } from 'react';
import { getPersonnel, createPersonnel, updatePersonnelStatus, getTeams } from '../services/api';
import { UserPlus, Search, ToggleLeft, ToggleRight } from 'lucide-react';

export default function Personnel() {
  const [personnel, setPersonnel] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '', role: 'operator', team_id: '', badge_number: '' });

  useEffect(() => {
    Promise.all([getPersonnel(), getTeams()]).then(([pRes, tRes]) => {
      setPersonnel(pRes.data.personnel || []);
      setTeams(tRes.data.teams || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    try {
      await createPersonnel(form);
      const res = await getPersonnel();
      setPersonnel(res.data.personnel || []);
      setShowAdd(false);
      setForm({ full_name: '', email: '', phone: '', password: '', role: 'operator', team_id: '', badge_number: '' });
    } catch (err) { alert(err.response?.data?.error || 'Failed to add personnel'); }
  };

  const toggleAvailability = async (id, current) => {
    await updatePersonnelStatus(id, !current);
    setPersonnel(prev => prev.map(p => p.id === id ? { ...p, is_available: !current } : p));
  };

  const filtered = personnel.filter(p =>
    p.full_name.toLowerCase().includes(search.toLowerCase()) ||
    p.email.toLowerCase().includes(search.toLowerCase()) ||
    p.team_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: 24, overflow: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 4 }}>Personnel</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{personnel.length} total • {personnel.filter(p => p.is_available).length} available</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted)' }} />
            <input className="form-input" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 30, width: 200 }} />
          </div>
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
            <UserPlus size={14} /> Add Personnel
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="panel" style={{ marginBottom: 20, padding: 20 }}>
          <div className="panel-title" style={{ marginBottom: 16 }}>NEW PERSONNEL</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <div><label className="form-label">Full Name *</label><input className="form-input" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></div>
            <div><label className="form-label">Email *</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="form-label">Phone *</label><input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className="form-label">Password *</label><input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <label className="form-label">Role</label>
              <select className="form-input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="operator">Operator</option>
                <option value="dispatcher">Dispatcher</option>
                <option value="field_agent">Field Agent</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="form-label">Team</label>
              <select className="form-input" value={form.team_id} onChange={e => setForm({ ...form, team_id: e.target.value })}>
                <option value="">-- Unassigned --</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div><label className="form-label">Badge Number</label><input className="form-input" value={form.badge_number} onChange={e => setForm({ ...form, badge_number: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleAdd}>Save Personnel</button>
            <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}>
              {['Name', 'Role', 'Team', 'Badge', 'Phone', 'Status', ''].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', fontWeight: 600 }}>{p.full_name}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{p.role?.replace('_', ' ')}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{p.team_name || '—'}</td>
                <td style={{ padding: '12px 16px', fontFamily: 'var(--font-display)', fontSize: 11 }}>{p.badge_number || '—'}</td>
                <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{p.phone}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    color: p.is_available ? 'var(--accent-emerald)' : 'var(--text-muted)',
                    fontSize: 11, fontWeight: 600
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.is_available ? 'var(--accent-emerald)' : 'var(--text-muted)' }} />
                    {p.is_available ? 'Available' : 'Offline'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <button className="btn" style={{ padding: '4px 10px', fontSize: 11 }}
                    onClick={() => toggleAvailability(p.id, p.is_available)}>
                    {p.is_available ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No personnel found</div>
        )}
      </div>
    </div>
  );
}
